import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Helmet } from "react-helmet-async";
const SalesforcePlatforms = () => {
  const phrases = [
    "Enterprise Cloud Architectures",
    "Lightning Component Frameworks",
    "MuleSoft Ingestion Pipelines"
  ];

  const cardData = [
    { type: 'sf-core', content: '☁️' },
    { type: 'cloud-wave', content: '📊' },
    { type: 'apex-stack', content: '⚙️' },
    { type: 'lwc-spark', content: '⚡' }
  ];

  const [currentPhraseIndex, setCurrentPhraseIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [cardIndex, setCardIndex] = useState(0);

  useEffect(() => {
    const currentPhrase = phrases[currentPhraseIndex];
    let timer;

    const handleTyping = () => {
      if (!isDeleting) {
        setDisplayedText(currentPhrase.substring(0, displayedText.length + 1));
        if (displayedText === currentPhrase) {
          timer = setTimeout(() => setIsDeleting(true), 1600);
          return;
        }
      } else {
        setDisplayedText(currentPhrase.substring(0, displayedText.length - 1));
        if (displayedText === '') {
          setIsDeleting(false);
          setCurrentPhraseIndex((prev) => (prev + 1) % phrases.length);
          return;
        }
      }

      const speed = isDeleting ? 30 : 80;
      timer = setTimeout(handleTyping, speed);
    };

    timer = setTimeout(handleTyping, 100);
    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentPhraseIndex]);

  useEffect(() => {
    const cardTimer = setInterval(() => {
      setCardIndex((prev) => (prev + 1) % cardData.length);
    }, 1100);
    return () => clearInterval(cardTimer);
  }, []);

  // Intersection Observer for Scroll Animations
  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-visible');
        }
      });
    }, observerOptions);

    const animatedElements = document.querySelectorAll(
      '.scroll-anim-fade-in, .scroll-anim-fade-in-up, .scroll-anim-fade-in-left, .scroll-anim-fade-in-right, .scroll-anim-flip, .scroll-anim-zoom-in'
    );

    animatedElements.forEach((el) => observer.observe(el));

    return () => {
      animatedElements.forEach((el) => observer.unobserve(el));
    };
  }, []);

  // Content Data
  const coreServices = [
    { icon: "☁️", title: "Core Cloud Implementation & Setup", desc: "Design, configure, and optimize mission-critical instances across Sales Cloud, Service Cloud, Experience Cloud, and targeted Industry Vertical packages." },
    { icon: "⚡", title: "Custom LWC & Apex Architecture", desc: "Develop high-performance, responsive Lightning Web Components and secure, bulkified Apex backend triggers following clean-code enterprise standards." },
    { icon: "⚙️", title: "OmniStudio & Declarative Automation", desc: "Construct scalable, maintainable automation topologies utilizing Salesforce Flows, OmniScripts, Integration Procedures, and DataRaptor mappings." },
    { icon: "📊", title: "Data Cloud & Real-Time CDP Hubs", desc: "Unify fragmented corporate interaction records into a singular, comprehensive profile view utilizing automated ingestion and mapping keys." },
    { icon: "🔗", title: "MuleSoft & API System Integration", desc: "Architect real-time bidirectional messaging conduits bridging core customer records to third-party ERP platforms, custom web states, and legacy storage hubs." },
    { icon: "🛡️", title: "Shield Security & Identity Governance", desc: "Enforce strict encryption schemas, real-time event monitoring metrics, field-level security constraints, and automated transaction logging profiles." },
    { icon: "🔄", title: "DevOps Pipeline Automation (SFDX)", desc: "Establish error-free source-driven deployment flows via scratch org strategies, automated testing validation blocks, and unified GitHub actions." }
  ];

  const advancedServices = [
    { icon: "🔍", title: "Intelligent AI Strategy & Agentforce", desc: "Deploy contextual agent frameworks to resolve complex inbound case queries automatically while maintaining continuous routing safety parameters." },
    { icon: "💼", title: "CPQ & Advanced Revenue Orchestration", desc: "Structure complex nested product catalogs, multi-tiered discount metrics, and asset amendment cycles inside an audited quoting interface." },
    { icon: "🗃️", title: "Large Data Volume (LDV) Optimization", desc: "Design customized skin archiving scripts and complex indexing properties to keep transaction latency minimal across massive database counts." },
    { icon: "📐", title: "Multi-Org Consolidation & Cleanup", desc: "Deconstruct legacy technical debt setups by merging disparate geographical business org configurations into streamlined parent instances." },
    { icon: "🚨", title: "Real-Time Health Cloud Coordination", desc: "Map specialized provider record files, internal clinical scheduling objects, and external diagnostic data feeds into unified profiles." },
    { icon: "📝", title: "Financial Services Cloud Compliance", desc: "Configure high-security household financial grouping tracking modules, automated document checklists, and rigorous cross-border privacy parameters." },
    { icon: "🌐", title: "Marketing Cloud Engagement Vectors", desc: "Build intricate transactional customer journey paths across SMS, push notification layers, and encrypted dynamic corporate mail scripts." },
    { icon: "🧪", title: "Automated Mock Unit Data Generation", desc: "Program flexible data factory scripts to validate trigger limits and ensure absolute system safety boundaries prior to production pushing." },
    { icon: "📦", title: "AppExchange Product Development", desc: "Package specialized standalone tools up to managed ISV configurations ready to pass rigorous Salesforce security validation audits cleanly." }
  ];

  const technologies = {
    "Core Clouds & Engines": ["Sales & Service Cloud Core", "Experience Cloud Communities", "Health / Financial Services Cloud", "Data Cloud Unified Streams"],
    "Custom Programming": ["Lightning Web Components (LWC)", "Apex Classes / Batch / Triggers", "SOQL / SOSL Query Design", "Visualforce / Aura Legacy Wrappers"],
    "Declarative Automation": ["Salesforce Flow Orchestrator", "OmniStudio OmniScript / FlexCards", "DataRaptors & Integration Procedures", "Approval Processes & Layouts"],
    "Integration & Pipelines": ["MuleSoft Anypoint Integration", "RESTful / SOAP Apex Endpoint APIs", "Salesforce Pub/Sub Streaming API", "Copado / Gearset / SFDX CLI Hubs"],
    "Intelligence & Security": ["Agentforce AI / Prompt Builder", "CRM Analytics / Tableau Lenses", "Salesforce Shield / Event Monitoring", "Field Level Security (FLS) Blocks"]
  };

  const processSteps = [
    { num: "01", title: "Org Discovery & Limit Assessment", desc: "Analyzing custom code metrics, checking governor constraint utilization thresholds, and listing system technical debt items." },
    { num: "02", title: "Data Model & Schema Architecture", desc: "Structuring optimized custom object profiles, setting strict lookup relationships, and defining master-detail indexing parameters." },
    { num: "03", title: "Declarative Flow & Apex Synthesis", desc: "Coding bulkified programmatic workflows, assembling modular visual blocks, and crafting unified asynchronous engines." },
    { num: "04", title: "MuleSoft System Interfacing", desc: "Mapping transactional JSON/XML structures, building out proxy security keys, and handling payload transformation processes." },
    { num: "05", title: "Security Testing & Profiles Audit", desc: "Validating object accessibility rules, verifying sharing models, and running full automation load tests over sandboxes." },
    { num: "06", title: "Source-Driven Production Package", desc: "Running absolute regression suite assertions and performing zero-downtime metadata pushes through verified DevOps pipelines." },
    { num: "07", title: "Continuous Optimization Review", desc: "Evaluating automation runtime speeds, debugging error logs via custom event tracing, and applying patch releases." }
  ];

  const industries = [
    "High-Velocity Financial Banking & FinTech Platforms", "Global Telecommunications & Managed Network Groups", "Cross-Border Specialized Healthcare & Diagnostics Network",
    "High-Density Retail, E-Commerce & Distribution Groups", "Aerospace, Heavy Automotive & Field Manufacturing", "High-Growth Software-as-a-Service (SaaS) Corporations",
    "Commercial Real Estate & Property Management Networks", "Public Sector Municipality & Agency Administration", "Energy Infrastructure, Utilities & Asset Allocation",
    "Global Logistics, Supply Tracking & Cargo Hubs", "Media, Digital Advertising & Broadcast Conglomerates", "Higher Education, Enrollment & Campus Lifecycle Systems"
  ];

  const designBenefits = [
    "100% Granular Customer Interaction Visibility", "Up to 45% Drop in Core Customer Case Resolution Latency", "Zero Fragmented Data Redundancies Across Cloud Layers",
    "Drastic Reductions in Manual System Administrative Tasks", "Airtight SOC2 & HIPAA Compliant Customer Security Profiles", "Scalable System Architecture Built for Large Data Volumes",
    "Drastic Optimizations in Internal Sales Conversion Ratios", "Instant AI-Driven Inbound Case Handling & Safe Routing Paths"
  ];

  const whyChooseUs = [
    "Certified Salesforce Application & System Architects", "Proven Scaling Track Record Across Large Data Volumes", "Airtight Zero-Trust Data Shield Custom Governance",
    "Deep Competency in MuleSoft & Enterprise ERP Linking", "Platform-Agnostic Hybrid System Interfacing Models", "Resilient Programmatic Apex & Responsive LWC Logic",
    "End-to-End Environment Lifecycle & DevOps Pipeline Management", "Transparent Project Milestones & Clear Sprint Performance Metrics", "High-Availability Platform Ingestion Infrastructure SLAs",
    "Experienced Data Cloud & Analytics Platform Integrators", "Iterative Sandbox Prototyping and Performance Validation Sprints", "100% Production Environment Core Stability Assurances"
  ];

  return (
    <>

      <Helmet>
        {/* Primary SEO */}
        <title>
          Salesforce Platform Services | Salesforce Consulting & CRM Solutions | Affsoft IT Solutions Pvt. Ltd.
        </title>

        <meta
          name="title"
          content="Salesforce Platform Services | Salesforce Consulting & CRM Solutions | Affsoft IT Solutions Pvt. Ltd."
        />

        <meta
          name="description"
          content="Affsoft IT Solutions Pvt. Ltd. provides Salesforce Platform services, including Salesforce consulting, CRM implementation, Sales Cloud, Service Cloud, Experience Cloud, Marketing Cloud, Salesforce customization, integration, automation, and managed support."
        />

        <meta
          name="keywords"
          content="Salesforce Platform, Salesforce Services, Salesforce Consulting, Salesforce CRM, Salesforce Implementation, Sales Cloud, Service Cloud, Experience Cloud, Marketing Cloud, Salesforce Integration, Salesforce Customization, Salesforce Development, CRM Solutions, Salesforce Automation, Salesforce Managed Services, Enterprise CRM, Salesforce Consulting Company, Affsoft IT Solutions Pvt. Ltd."
        />

        <meta name="robots" content="index, follow" />
        <meta name="author" content="Affsoft IT Solutions Pvt. Ltd." />
        <meta name="language" content="English" />
        <meta name="theme-color" content="#0d5c53" />

        {/* Canonical */}
        <link
          rel="canonical"
          href="https://www.affsoft.com/salesforce-platforms"
        />

        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta
          property="og:site_name"
          content="Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:title"
          content="Professional Salesforce Platform Services | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          property="og:description"
          content="Empower your business with Salesforce Platform services from Affsoft IT Solutions Pvt. Ltd. We specialize in Salesforce CRM consulting, implementation, customization, integration, Sales Cloud, Service Cloud, and enterprise automation."
        />
        <meta
          property="og:url"
          content="https://www.affsoft.com/salesforce-platforms"
        />
        <meta
          property="og:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Salesforce Platform Services | Salesforce Consulting & CRM Solutions | Affsoft IT Solutions Pvt. Ltd."
        />
        <meta
          name="twitter:description"
          content="Drive customer success with Salesforce Platform services from Affsoft IT Solutions Pvt. Ltd. We offer Salesforce CRM consulting, implementation, integration, customization, automation, and managed services."
        />
        <meta
          name="twitter:image"
          content="https://www.affsoft.com/images/affsoft-logo.png"
        />
      </Helmet>
      <div className="salesforce-services-page bg-light" style={{ fontFamily: '"Inter", sans-serif', overflowX: 'hidden' }}>

        {/* ================= HERO SECTION ================= */}
        <div
          className="hero-container container-fluid d-flex align-items-center justify-content-center position-relative overflow-hidden"
          style={{
            backgroundImage: "url('./images/home.jpg')",
            backgroundRepeat: 'no-repeat',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minHeight: '85vh',
            backgroundColor: '#0a1d37' // Deep Salesforce blue slate fallback
          }}
        >
          {/* Background Grid Lines */}
          <div className="grid-overlay position-absolute top-0 start-0 w-100 h-100" style={{ opacity: 0.12 }}>
            <div className="grid-line line-v-1"></div>
            <div className="grid-line line-v-2"></div>
            <div className="grid-line line-v-3"></div>
          </div>

          {/* Infinite Marquee Text */}
          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in" style={{ top: '15%', opacity: 0.05 }}>
          </div>

          <div className="marquee-wrapper select-none position-absolute w-100 scroll-anim-fade-in">
            <div className="marquee-content text-white">
              <span>Salesforce &nbsp; Platform &nbsp; Systems &nbsp; &amp; &nbsp;</span>
              <span>Enterprise Cloud Ecosystems </span>
            </div>
          </div>

          {/* Main Content Layout */}
          <div className="container position-relative z-3 my-5">
            <div className="row align-items-center py-4">

              {/* Left Side Info */}
              <div className="col-12 col-md-5 text-start d-flex flex-column align-items-start hero-left-pane text-white mb-4 mb-md-0 scroll-anim-fade-in-left">
                <div className="asterisk-icon mb-3 text-cyan fs-3" style={{ color: '#00a1e0' }}>✳</div>
                <h1 className="fw-bold mb-3 display-5">Salesforce Platforms</h1>
                <p className="tagline mb-4 text-white" style={{ fontSize: '1rem', lineHeight: '1.6', opacity: 0.9 }}>
                  At Affsoft IT Solutions Pvt. Ltd., we transform disjointed account tracking sheets into automated corporate engines. Our Salesforce architecture practice unifies programmatic Apex development and responsive LWC panels with real-time Data Cloud structures and MuleSoft pipelines to convert core CRM instances into lightning-fast strategic spaces.
                </p>
                <button className="btn btn-outline-light d-flex align-items-center gap-2 px-4 py-2 rounded-pill fw-semibold">
                  <span>➔</span>
                  <span>Propose a Cloud Blueprint</span>
                </button>
              </div>

              {/* Center Section */}
              <div className="col-12 col-md-4 text-center mb-4 mb-md-0 hero-center-pane scroll-anim-flip">
                <div className="typing-badge px-4 py-3 mx-auto bg-dark bg-opacity-75 rounded-pill border border-secondary shadow-lg d-inline-block">
                  <span className="typed-text text-white fw-mono fs-4">{displayedText}</span>
                  <span className="cursor fs-4 animate-blink" style={{ color: '#00a1e0' }}>|</span>
                </div>
              </div>

              {/* Right Section */}
              <div className="col-12 col-md-3 d-flex justify-content-center justify-content-md-end hero-right-pane scroll-anim-fade-in-right">
                <div className="glass-card-frame shadow-lg p-3 bg-white bg-opacity-10 rounded-4 border border-light" style={{ width: '220px', backdropFilter: 'blur(10px)' }}>
                  <div className={`inner-graphic-card graphic-${cardData[cardIndex].type} d-flex align-items-center justify-content-center rounded-3 bg-dark`} style={{ height: '180px' }}>
                    <span className="graphic-icon display-3">{cardData[cardIndex].content}</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>

        {/* ================= CORE SOLUTIONS SECTION ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#00a1e0' }}>CRM Architecture Scaling</span>
              <h2 className="fw-bold text-dark display-6">Deploy Upgrade-Stable Architecture & Intelligent Customer Data Nodes</h2>
              <p className="text-muted mt-3">
                Unoptimized row-locking logic, fragile un-bulkified database triggers, and out-of-sync external systems can heavily impact user operations. At Affsoft IT Solutions Pvt. Ltd., we engineer clean-core, secure, and systematically scaled Salesforce infrastructure networks.
              </p>
            </div>

            <div className="row g-4">
              {coreServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#00a1e0' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Business Value Checklist */}
            <div className="mt-5 p-4 rounded-4 bg-light border scroll-anim-fade-in-up">
              <h5 className="fw-bold text-center text-dark mb-4">Core Operational Gains of Disciplined Salesforce Architecture Engineering</h5>
              <div className="row g-3">
                {designBenefits.map((benefit, index) => (
                  <div className="col-6 col-md-3 d-flex align-items-center gap-2" key={index}>
                    <span className="text-success fs-5">✓</span>
                    <span className="text-secondary small fw-medium">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= ADVANCED SOLUTIONS OPTIONS ================= */}
        <section className="py-5 bg-light">
          <div className="container">
            <div className="text-center max-width-700 mx-auto mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase text-white" style={{ backgroundColor: '#00a1e0' }}>Distributed Cloud Layers</span>
              <h2 className="fw-bold text-dark display-6">Adaptive Platform Capabilities Built for High Transaction Volume</h2>
              <p className="text-muted mt-3">
                We scale baseline out-of-the-box system layouts into highly resilient, automated business operations. From mapping contextual Agentforce setups to structural CPQ engine cleanups, our solutions keep enterprise workflows performing cleanly.
              </p>
            </div>

            <div className="row g-4">
              {advancedServices.map((service, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-zoom-in" key={index} style={{ animationDelay: `${index * 0.1}s` }}>
                  <div className="card h-100 shadow-sm border-0 rounded-4 p-4 bg-white hover-translate transition-all">
                    <div className="fs-1 mb-3" style={{ color: '#00a1e0' }}>{service.icon}</div>
                    <h4 className="fw-bold fs-5 mb-2 text-dark">{service.title}</h4>
                    <p className="text-muted mb-0 small">{service.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= TECHNOLOGIES SECTION ================= */}
        <section className="py-5 bg-dark text-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge px-3 py-2 rounded-pill mb-2 text-uppercase fw-bold text-dark" style={{ backgroundColor: '#00a1e0' }}>Ecosystem Core Stack</span>
              <h2 className="fw-bold display-6">Salesforce Infrastructure & Automation Matrix We Leverage</h2>
              <p className="text-light-50">We build, configure, and manage high-volume customer networks utilizing verified sandbox life cycles.</p>
            </div>

            <div className="row g-4 justify-content-center">
              {Object.entries(technologies).map(([category, list], index) => (
                <div className="col-12 col-sm-6 col-md-4 col-lg scroll-anim-fade-in" key={index}>
                  <div className="bg-secondary bg-opacity-25 p-4 rounded-4 h-100 border border-secondary border-opacity-50">
                    <h5 className="fw-bold mb-3 border-bottom border-secondary pb-2" style={{ color: '#00a1e0' }}>{category}</h5>
                    <ul className="list-unstyled mb-0">
                      {list.map((tech, i) => (
                        <li key={i} className="mb-2 d-flex align-items-center gap-2 small">
                          <span className="opacity-75" style={{ color: '#00a1e0' }}>•</span> {tech}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= ROADMAP PROCESS ================= */}
        <section className="py-5 bg-white">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <span className="badge bg-secondary px-3 py-2 rounded-pill mb-2 text-uppercase">Environment Lifecycle</span>
              <h2 className="fw-bold text-dark display-6">Our Engineered Cloud Deployment Blueprint</h2>
              <p className="text-muted">A structured, strictly governed development roadmap designed to transition modern functional scripts safely from sandbox testing onto live multi-region instances.</p>
            </div>

            <div className="row g-3">
              {processSteps.map((step, index) => (
                <div className="col-12 col-md-6 col-lg scroll-anim-fade-in-up" key={index} style={{ animationDelay: `${index * 0.15}s` }}>
                  <div className="p-4 rounded-4 bg-light border-start border-4 h-100 shadow-sm" style={{ borderLeftColor: '#00a1e0' }}>
                    <div className="fw-bold display-6 mb-2" style={{ color: '#00a1e0' }}>{step.num}</div>
                    <h5 className="fw-bold text-dark fs-6">{step.title}</h5>
                    <p className="text-muted small mb-0">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= INDUSTRIES SERVED ================= */}
        <section className="py-5 bg-light">
          <div className="container text-center">
            <h2 className="fw-bold text-dark mb-4 display-6 scroll-anim-fade-in-up">Ecosystems We Empower</h2>
            <div className="d-flex flex-wrap justify-content-center gap-2 max-width-900 mx-auto scroll-anim-zoom-in">
              {industries.map((industry, index) => (
                <span className="badge bg-white text-dark border px-3 py-2 rounded-pill shadow-sm fs-7 fw-medium" key={index}>
                  🏢 {industry}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* ================= WHY CHOOSE AFFSOFT ================= */}
        <section className="py-5 bg-white border-top">
          <div className="container">
            <div className="text-center mb-5 scroll-anim-fade-in-up">
              <h2 className="fw-bold text-dark display-6">Why Partner with Affsoft IT Solutions Pvt. Ltd.?</h2>
              <p className="text-muted">We combine programmatic architectural mastery with metadata-driven version tracking to guarantee stable enterprise software behavior.</p>
            </div>

            <div className="row g-3">
              {whyChooseUs.map((reason, index) => (
                <div className="col-12 col-md-6 col-lg-4 scroll-anim-fade-in-left" key={index} style={{ animationDelay: `${(index % 3) * 0.1}s` }}>
                  <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-3 h-100">
                    <span className="fs-4" style={{ color: '#00a1e0' }}>🛡️</span>
                    <span className="text-dark fw-semibold small">{reason}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Custom Styles Injection */}
        <style>{`
        .hover-translate {
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }
        .hover-translate:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
        .text-light-50 {
          color: rgba(255, 255, 255, 0.7);
        }
        .max-width-700 {
          max-width: 700px;
        }
        .max-width-900 {
          max-width: 900px;
        }
        .animate-blink {
          animation: blink 1s step-end infinite;
        }
        @keyframes blink {
          from, to { color: transparent }
          50% { color: inherit }
        }

        /* --- Scroll Animation Core --- */
        .scroll-anim-fade-in,
        .scroll-anim-fade-in-up,
        .scroll-anim-fade-in-left,
        .scroll-anim-fade-in-right,
        .scroll-anim-flip,
        .scroll-anim-zoom-in {
          opacity: 0;
          will-change: transform, opacity;
          transition: all 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        }

        /* Visibility Trigger via IntersectionObserver */
        .animate-visible {
          opacity: 1 !important;
          transform: none !important;
        }

        /* --- Animations Configurations --- */
        .scroll-anim-fade-in-up {
          transform: translateY(40px);
        }
        .scroll-anim-fade-in-left {
          transform: translateX(-50px);
        }
        .scroll-anim-fade-in-right {
          transform: translateX(50px);
        }
        .scroll-anim-zoom-in {
          transform: scale(0.85);
        }
        .scroll-anim-flip {
          transform: perspective(400px) rotateY(90deg);
          transition: all 1s cubic-bezier(0.175, 0.885, 0.32, 1.275);
        }
        
        .scroll-anim-flip.animate-visible {
          transform: perspective(400px) rotateY(0deg) !important;
        }
      `}</style>

      </div>

    </>
  );
};

export default SalesforcePlatforms;